import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DateRange } from 'react-day-picker'

import type { TenantKey } from '@/config/types'
import { getBranchesByTenant, getDefaultBranchType } from '@/utils'
import { DateType } from '@/utils/date'
import { RootState } from './store'

// This allows filterSlice to initialize with the correct default branch.
const detectTenant = (): TenantKey => {
  const forced = import.meta.env.VITE_FORCE_TENANT as TenantKey | undefined
  if (forced) return forced

  const host = window.location.hostname
  if (host.includes('blankstreet')) return 'blankstreet'
  if (host.includes('dev')) return 'dev'

  return 'dev'
}

/**
 * Branch selection type
 * 'all': All stores selected
 * 'same': Same stores selected (existing stores)
 * 'new': New stores selected
 * 'regular': Branch selection outside of all, same, or new
 */
export type BranchType = 'all' | 'same' | 'new' | 'regular'

/**
 * UNIFIED FILTER STATE
 *
 * This single state replaces 3 separate contexts (Base, Comparison, SalesLens).
 *
 * Core filters (used by all pages):
 * selectedBranch, selectedBranchType, date, dateFilterType
 *
 * Optional filters (used by specific page types):
 * comparisonBranch: For lens pages that need comparison (PnL Lens)
 * temperatureFilter, sizeFilter: For sales-lens pages (Sales Lens Items)
 * typeFilter, chartType: For lens pages with charts
 */
export interface FilterState {
  // Core filters - used by all pages
  selectedBranch: string
  selectedBranchType: BranchType
  date: DateRange | undefined
  dateFilterType: DateType
  // Comparison filter(PnL Lens)
  comparisonBranch?: string

  // Sales Lens specific filters
  temperatureFilter?: string
  sizeFilter?: string

  // Metric filters (for lens pages with charts)
  typeFilter?: string
  chartType?: string

  // Page specific defaults
  defaults: {
    defaultBranch: string
    defaultBranchType: BranchType
    defaultComparisonBranch?: string
    defaultDate: DateRange
    defaultDateFilterType: DateType
    defaultTemperatureFilter?: string
    defaultSizeFilter?: string
    defaultTypeFilter?: string
    defaultChartType?: string
  }

  // Refresh callback registry
  refreshCallbacks: Record<string, boolean>
}

/**
 * INITIAL STATE
 *
 * Default values when the app first loads.
 * selectedBranch and selectedBranchType are initialized using tenant detection.
 * - blankstreet: 'all' stores by default
 * - dev: 'same' stores by default
 */
const initialTenant = detectTenant()
// Get from sessionStorage to avoid circular dependency
const initialDefaultBranchValue = getBranchesByTenant(initialTenant)
const initialDefaultBranchType: BranchType = getDefaultBranchType(initialTenant)

const initialState: FilterState = {
  selectedBranch: initialDefaultBranchValue, // Joined branch names from sessionStorage
  selectedBranchType: initialDefaultBranchType,
  date: {
    from: new Date(2024, 6, 1), // July 1, 2024
    to: new Date(2024, 6, 26), // July 26, 2024
  },
  dateFilterType: 'month-to-date',
  comparisonBranch: '',
  temperatureFilter: undefined,
  sizeFilter: undefined,
  typeFilter: undefined,
  chartType: undefined,
  defaults: {
    defaultBranch: initialDefaultBranchValue,
    defaultBranchType: initialDefaultBranchType,
    defaultDate: {
      from: new Date(2024, 6, 1),
      to: new Date(2024, 6, 26),
    },
    defaultDateFilterType: 'month-to-date',
  },
  refreshCallbacks: {},
}

const filterSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    initializeFilters: (
      state,
      action: PayloadAction<{
        defaultBranch: string
        defaultBranchType: BranchType
        defaultComparisonBranch?: string
        defaultDate: DateRange
        defaultDateFilterType: DateType
        defaultTemperatureFilter?: string
        defaultSizeFilter?: string
        defaultTypeFilter?: string
        defaultChartType?: string
      }>
    ) => {
      const {
        defaultBranch,
        defaultBranchType,
        defaultComparisonBranch,
        defaultDate,
        defaultDateFilterType,
        defaultTemperatureFilter,
        defaultSizeFilter,
        defaultTypeFilter,
        defaultChartType,
      } = action.payload

      // Set current values to defaults
      state.selectedBranch = defaultBranch
      state.selectedBranchType = defaultBranchType

      // preserve date if filter context (default type) matches
      if (
        state.defaults?.defaultDateFilterType !== defaultDateFilterType ||
        !state.date ||
        (state.date.from === undefined && state.date.to === undefined)
      ) {
        state.date = defaultDate
        state.dateFilterType = defaultDateFilterType
      }

      // initialize comparison branch
      if (defaultComparisonBranch !== undefined) {
        if (!state.comparisonBranch) {
          state.comparisonBranch = defaultComparisonBranch
        }
      } else {
        state.comparisonBranch = undefined
      }

      // initialize sales lens filters
      if (defaultTemperatureFilter !== undefined) {
        if (!state.temperatureFilter) {
          state.temperatureFilter = defaultTemperatureFilter
        }
      } else {
        state.temperatureFilter = undefined
      }

      if (defaultSizeFilter !== undefined) {
        if (!state.sizeFilter) {
          state.sizeFilter = defaultSizeFilter
        }
      } else {
        state.sizeFilter = undefined
      }

      // initialize metric filters
      if (defaultTypeFilter !== undefined) {
        if (!state.typeFilter) {
          state.typeFilter = defaultTypeFilter
        }
      } else {
        state.typeFilter = undefined
      }

      if (defaultChartType !== undefined) {
        if (!state.chartType) {
          state.chartType = defaultChartType
        }
      } else {
        state.chartType = undefined
      }

      // store defaults for reset functionality
      state.defaults = {
        defaultBranch,
        defaultBranchType,
        defaultComparisonBranch,
        defaultDate,
        defaultDateFilterType,
        defaultTemperatureFilter,
        defaultSizeFilter,
        defaultTypeFilter,
        defaultChartType,
      }
    },

    setSelectedBranch: (
      state,
      action: PayloadAction<{ branch: string; branchType: BranchType }>
    ) => {
      state.selectedBranch = action.payload.branch
      state.selectedBranchType = action.payload.branchType
    },

    setSelectedBranchType: (state, action: PayloadAction<BranchType>) => {
      state.selectedBranchType = action.payload
    },

    setDate: (state, action: PayloadAction<DateRange | undefined>) => {
      state.date = action.payload
    },

    setDateFilterType: (state, action: PayloadAction<DateType>) => {
      state.dateFilterType = action.payload
    },

    /**
     * Comparison filter setter
     */
    setComparisonBranch: (state, action: PayloadAction<string>) => {
      state.comparisonBranch = action.payload
    },

    /**
     * Sales-lens filter setters
     */
    setTemperatureFilter: (state, action: PayloadAction<string>) => {
      state.temperatureFilter = action.payload
    },

    setSizeFilter: (state, action: PayloadAction<string>) => {
      state.sizeFilter = action.payload
    },

    /**
     * Metric filter setters
     */
    setTypeFilter: (state, action: PayloadAction<string>) => {
      state.typeFilter = action.payload
    },

    setChartType: (state, action: PayloadAction<string>) => {
      state.chartType = action.payload
    },

    /**
     * Reset filters to their default values
     * Uses the stored defaults from initialization
     */
    resetFilters: (state) => {
      state.selectedBranch = state.defaults.defaultBranch
      state.selectedBranchType = state.defaults.defaultBranchType
      state.date = state.defaults.defaultDate
      state.dateFilterType = state.defaults.defaultDateFilterType
      state.comparisonBranch = state.defaults.defaultComparisonBranch
      state.temperatureFilter = state.defaults.defaultTemperatureFilter
      state.sizeFilter = state.defaults.defaultSizeFilter
      state.typeFilter = state.defaults.defaultTypeFilter
      state.chartType = state.defaults.defaultChartType
    },

    /**
     * Trigger refresh for a specific page
     * Components will watch this flag to know when to refetch data
     */
    triggerRefresh: (state, action: PayloadAction<string>) => {
      const pageId = action.payload
      state.refreshCallbacks[pageId] = !state.refreshCallbacks[pageId]
    },
  },
})

export const {
  initializeFilters,
  setSelectedBranch,
  setSelectedBranchType,
  setDate,
  setDateFilterType,
  setComparisonBranch,
  setTemperatureFilter,
  setSizeFilter,
  setTypeFilter,
  setChartType,
  resetFilters,
  triggerRefresh,
} = filterSlice.actions

export default filterSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

// Core filters
export const selectSelectedBranch = (state: RootState) =>
  state.filters.selectedBranch
export const selectSelectedBranchType = (state: RootState) =>
  state.filters.selectedBranchType
export const selectDate = (state: RootState) => state.filters.date
export const selectDateFilterType = (state: RootState) =>
  state.filters.dateFilterType

// Comparison filters
export const selectComparisonBranch = (state: RootState) =>
  state.filters.comparisonBranch

// Sales Lens filters
export const selectTemperatureFilter = (state: RootState) =>
  state.filters.temperatureFilter
export const selectSizeFilter = (state: RootState) => state.filters.sizeFilter

// Metric filters
export const selectTypeFilter = (state: RootState) => state.filters.typeFilter
export const selectChartType = (state: RootState) => state.filters.chartType

// System state
export const selectRefreshCallbacks = (state: RootState) =>
  state.filters.refreshCallbacks
export const selectPageRefreshTrigger =
  (pageId: string) => (state: RootState) =>
    state.filters.refreshCallbacks[pageId]

// Generic selector for whole state (use carefully)
export const selectFilterState = (state: RootState) => state.filters
